import { SaveFilled } from "@ant-design/icons";
import { CheckCircle2, Files } from "lucide-react";
import { Button, Form, FormProps, Input, message, Select } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Folder } from "../../redux/interfaces/folder.types.ts";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { TemplateOption } from "./types.ts";
import {
  addTemplatesToFolder,
  getTemplatesNotInFolder,
} from "../../redux/actions/templatesActions.tsx";
import { TagOption } from "./TagOption.tsx";
import { AddParticipantToFolder } from "./AddParticipantToFolder.tsx";
import { fetchFolderById } from "../../redux/actions/vaultActions.tsx";
import { setActiveFolderAsync } from "../../redux/actions/folderActions.tsx";
import { validateEmail } from "../../utils/vault/generalValidators.ts";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";

// The top selector only offers the PRINCIPAL templates (one per folder): the main
// participants and the contract/collateral sections. Additional participants
// (tenant/guarantor "-cja" and secondary "landlord-*") live in the "Personas
// adicionales" card below, so they are intentionally excluded here.
const PRINCIPAL_TEMPLATE_SLUGS = [
  "tenant-pf",
  "tenant-pm",
  "guarantor-pf",
  "guarantor-pm",
  "lease-contract-pf",
  "lease-contract-pm",
  "collateral",
];

// PF/PM variants are mutually exclusive per role/section: once one is in the folder (or
// selected), the other variant is hidden from the selector.
const EXCLUSIVE_GROUPS: string[][] = [
  ["tenant-pf", "tenant-pm"],
  ["guarantor-pf", "guarantor-pm"],
  ["lease-contract-pf", "lease-contract-pm"],
];

interface Props {
  folder: Folder;
  closeModal: () => void;
}

type FormValues = {
  templates: string[];
  guarantor_email?: string;
  landlord_email?: string;
};

export const AddTemplatesToFolder: React.FC<Props> = ({
  folder,
  closeModal,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [templatesForm] = Form.useForm<FormValues>();

  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [templatesSelected, setTemplatesSelected] = useState<string[]>([]);

  const dispatch = useAppDispatch();
  const { loading, tagColors } = useAppSelector((state) => state.templates);

  const renderTagOption = useCallback(
    (props: any) => {
      const { label, closable, onClose } = props;
      const labelStr = typeof label === "string" ? label : String(label);
      const color = tagColors[labelStr];

      return (
        <TagOption
          label={label}
          closable={closable}
          onClose={onClose}
          color={color}
        />
      );
    },
    [tagColors],
  );

  const onLoadNewTemplates = async () => {
    try {
      const templates = await dispatch(
        getTemplatesNotInFolder(folder),
      ).unwrap();

      // Slugs already in the folder → if a PF/PM group already has a member, hide BOTH.
      const folderSlugs = new Set<string>([
        ...(folder.subfolders ?? []).map((sf) => sf.template.slug),
        ...(folder.templates ?? []).map((t) => t.slug),
      ]);
      const excludedBySibling = new Set<string>();
      EXCLUSIVE_GROUPS.forEach((group) => {
        if (group.some((slug) => folderSlugs.has(slug))) {
          group.forEach((slug) => excludedBySibling.add(slug));
        }
      });

      setTemplateOptions(
        templates
          .filter((template) =>
            PRINCIPAL_TEMPLATE_SLUGS.includes(template.slug),
          )
          .filter((template) => !excludedBySibling.has(template.slug))
          .map((template) => ({
            value: template.slug,
            label: template.name,
          })),
      );
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudieron cargar las plantillas."));
    }
  };

  const onAddTemplates: FormProps<FormValues>["onFinish"] = async ({
    templates,
    guarantor_email,
    landlord_email,
  }: any) => {
    try {
      await dispatch(
        addTemplatesToFolder({
          folderId: folder.id,
          templates,
          guarantor_email,
          landlord_email,
        }),
      ).unwrap();

      templatesForm.resetFields();

      messageApi.open({
        type: "success",
        content: `Se han agregado ${templates.length} plantillas al expediente.`,
      });

      const activeFolder = await dispatch(fetchFolderById(folder.id)).unwrap();
      await dispatch(setActiveFolderAsync(activeFolder));
      closeModal();
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudieron agregar las plantillas."));
    }
  };

  useEffect(() => {
    if (folder) onLoadNewTemplates();
  }, [folder]);

  // While selecting, hide the PF/PM sibling of any chosen template (can't add both).
  const visibleOptions = useMemo(() => {
    const selectedSiblings = new Set<string>();
    EXCLUSIVE_GROUPS.forEach((group) => {
      group.forEach((slug) => {
        if (templatesSelected.includes(slug)) {
          group.forEach((sibling) => {
            if (sibling !== slug) selectedSiblings.add(sibling);
          });
        }
      });
    });
    return templateOptions.filter((o) => !selectedSiblings.has(o.value));
  }, [templateOptions, templatesSelected]);

  return (
    <div className="w-full space-y-4">
      {contextHolder}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-start gap-3 mb-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-[#28397a] shrink-0">
            <Files className="w-5 h-5" />
          </span>
          <div>
            <h3 className="font-semibold text-slate-800 leading-tight">
              Plantillas principales
            </h3>
            <p className="text-sm text-slate-500">
              Participantes principales y secciones del expediente (contrato,
              garantía). Cada una se agrega una sola vez.
            </p>
          </div>
        </div>
        {templateOptions.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Todas las plantillas principales ya están en el expediente.
          </div>
        ) : (
        <Form
          name="form"
          form={templatesForm}
          onFinish={onAddTemplates}
          className="w-full mx-auto"
        >
          <Form.Item<FormValues>
            label="Selecciona una o varias plantillas"
            name="templates"
            labelCol={{ span: 24 }}
            rules={[
              {
                required: true,
                message: "Campo requerido",
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Inquilino PF"
              tagRender={renderTagOption}
              onChange={setTemplatesSelected}
              className="w-full"
              options={visibleOptions}
            />
          </Form.Item>

          {(templatesSelected.includes("guarantor-pm") ||
            templatesSelected.includes("guarantor-pf") ||
            templatesSelected.includes("guarantor-pf-cja") ||
            templatesSelected.includes("guarantor-pm-cja")) && (
            <Form.Item>
              <Form.Item<FormValues>
                label="Correo del Fiador/Obliagado solidario"
                labelCol={{ span: 24 }}
                name="guarantor_email"
                validateTrigger={["onBlur", "onFinish"]}
                rules={[
                  {
                    required: false,
                  },
                  {
                    type: "email",
                  },
                  { validator: validateEmail },
                ]}
              >
                <Input type="email" className="lowercase" />
              </Form.Item>
            </Form.Item>
          )}

          {(templatesSelected.includes("lease-contract-pm") ||
            templatesSelected.includes("lease-contract-pf")) && (
            <Form.Item>
              <Form.Item<FormValues>
                label="Correo del propietario"
                labelCol={{ span: 24 }}
                name="landlord_email"
                validateTrigger={["onBlur", "onFinish"]}
                rules={[
                  {
                    required: false,
                  },
                  {
                    type: "email",
                  },
                  { validator: validateEmail },
                ]}
              >
                <Input type="email" className="lowercase" />
              </Form.Item>
            </Form.Item>
          )}

          <Form.Item>
            <Button
              loading={loading}
              icon={<SaveFilled />}
              type="primary"
              htmlType="submit"
            >
              Agregar plantillas
            </Button>
          </Form.Item>
        </Form>
        )}
      </div>

      <AddParticipantToFolder folder={folder} />
    </div>
  );
};
